/* eslint-disable dot-notation */
/* eslint-disable max-len */
import { ofType } from 'redux-observable';
import { merge } from 'rxjs';
import { switchMap, mergeMap, catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';
import { fromPromise } from 'rxjs/observable/fromPromise';
import router from 'next/router';

import { sentryConfigureScope } from '@src/utils/sentry';
import { session, user, ui } from '@src/services';
import * as actions from '@src/store/actions';
import { updateCartProductsCall } from '@src/store/actions/cart';
import { learningProgressDone } from '@src/store/actions/learningPath';
import { reactLocalStorage } from '@src/utils/localStorage';
import { getPasswordPolicyErrorMessageKey } from '@src/utils/passwordPolicy';
import { clearLineLoginCallback, saveLineLoginCallback, takeLineLoginCallback } from '@src/utils/lineLoginCallback';
import * as types from '@src/store/types';
import { TOAST_TYPE_SUCCESS, TOAST_TYPE_ERROR } from '@src/const/common';
import { PUNCH_MACHINE_CB_KEY } from '@src/components/hr/punchInMachine/constants';
import * as apiResponseCache from '@src/db/apiResponseCache';

const getRegistrationOnboardingPath = registrationLinkId => `/hr/registration-link/${registrationLinkId}`;

const redirectToRegistrationOnboardingIfNeeded = (profile, cb) => {
	if (typeof window === 'undefined') return false;
	if (!profile?.is_registering) return false;
	if (typeof profile?.registration_link_id !== 'string' || !profile.registration_link_id) return false;

	const targetPath = getRegistrationOnboardingPath(profile.registration_link_id);
	const isSameRegistrationLinkPage = router?.pathname === '/hr/registration-link/[id]' &&
		router?.query?.id === profile.registration_link_id;

	if (router?.asPath === targetPath || isSameRegistrationLinkPage) return false;

	// 已登入但 registering 的人從打卡機被導去 onboarding 時，記住打卡機入口讓完成頁導回；
	// 非打卡機來源則清掉殘留，避免誤導回打卡機。
	const sourcePath = cb || router?.asPath;
	if (sourcePath?.startsWith('/hr/punch-in/machine')) {
		reactLocalStorage.set(PUNCH_MACHINE_CB_KEY, sourcePath);
	} else {
		reactLocalStorage.remove(PUNCH_MACHINE_CB_KEY);
	}

	router.replace(targetPath);
	return true;
};

export const checkLoginCall = action$ => (
	action$.pipe(
		ofType(types.CHECK_LOGIN_CALL),
		switchMap(({ payload: { ssrHeaders, onSuccess, onError } }) => (
			fromPromise(user.getProfile({ ssrHeaders })).pipe(
				switchMap(res => {
					const isBrowser = typeof window !== 'undefined';
					const isHr = isBrowser && router?.pathname?.startsWith('/hr');
					redirectToRegistrationOnboardingIfNeeded(res);

					const actionsToDispatch = [
						...(!isHr ? [updateCartProductsCall()] : []),
						actions.checkLoginDone(res),
						actions.setUser({ profile: res }),
						actions.setModalType({ type: null }),
					];

					return of(...actionsToDispatch).pipe(
						// 在所有 actions 都 dispatch 後才執行 callback
						tap({ complete: () => onSuccess?.(res) }),
					);
				}),
				catchError(err => {
					if (onError) onError(err);
					return of(actions.checkLoginFail(err));
				}),
			)
		)),
	)
);

export const checkLoginSessionCall = action$ => (
	action$.pipe(
		ofType(types.CHECK_LOGIN_SESSION_CALL),
		switchMap(({ payload }) => (
			fromPromise(user.getProfile({})).pipe(
				switchMap(res => {
					const savedLineCb = takeLineLoginCallback(payload?.lineLoginState);
					const lineCb = payload?.cb ? null : savedLineCb;
					const isRedirectedToOnboarding = redirectToRegistrationOnboardingIfNeeded(res, lineCb);
					const stashActions = !lineCb && localStorage.getItem('STASH_KEY');
					const nextActions = [
						// LINE 返回入口優先於其他流程留下的 actions，避免舊導頁蓋過打卡／onboarding。
						...(lineCb ? [actions.clearStash()] : []),
						actions.checkLoginSessionDone(res),
						actions.setUser({ profile: res }),
						actions.toastShowCall({ type: TOAST_TYPE_SUCCESS, message: 'share:auth:message:login_success' }),
						...(!stashActions && !isRedirectedToOnboarding ? [actions.historyPushCall(lineCb || payload?.cb || '/')] : []),
					];
					return nextActions;
				}),
				catchError(err => {
					clearLineLoginCallback(payload?.lineLoginState);
					return of(actions.checkLoginSessionFail(err));
				}),
			)
		)),
	)
);

// payload（皆為選填）：
//   redirect —— 指定登出後導向的路徑，覆蓋預設的 closed_web / fromCompanyVat / '/' 判斷
//   silent   —— 不跳「登出成功」toast，給「登出只是附帶效果」的流程用（如 LINE 解除綁定）
export const logoutCall = (action$, store) => (
	action$.pipe(
		ofType(types.LOGOUT_CALL),
		switchMap(({ payload }) => {
			const { redirect, silent } = payload || {};
			const closedWeb = store.value?.theme?.themes?.closed_web || false;
			const isBrowser = typeof window !== 'undefined';
			const isFromCompanyVat = isBrowser && window.location.search.includes('fromCompanyVat=');
			const cb = isBrowser ? encodeURIComponent(window.location.href) : '';
			// HR 頁面不需要 cart state，比照 checkLoginCall 跳過 updateCartProductsCall。
			const isHr = isBrowser && router?.pathname?.startsWith('/hr');
			// __anon bucket 跨帳號共用，登出必須主動清，否則下個 user 的 __anon fallback 會讀到本 user 的快取。
			// 真實 accountId bucket 由 TTL 淘汰，不在這裡動。
			if (isBrowser) apiResponseCache.clearAccount(apiResponseCache.ANON_ACCOUNT_ID);
			const doneActions = [
				...(!isHr ? [updateCartProductsCall()] : []),
				actions.logoutDone(),
				...(silent ? [] : [actions.toastShowCall({ type: TOAST_TYPE_SUCCESS, message: 'share:auth:message:logout_success' })]),
				learningProgressDone({ res: { joined: false, progress: [] } }),
			];
			if (redirect) doneActions.push(actions.historyPushCall(redirect));
			else if (closedWeb || isHr) doneActions.push(actions.historyPushCall(`/business-login?cb=${cb}`));
			else if (isFromCompanyVat) doneActions.push(actions.setModalType({ type: 'login' }));
			else doneActions.push(actions.historyPushCall('/'));

			// 真正呼叫後端 logout，讓 refresh_token cookie 失效；不論成功失敗都要完成清理。
			return fromPromise(session.logout().catch(() => null)).pipe(
				switchMap(() => {
					sentryConfigureScope(scope => scope.clear());
					return doneActions;
				}),
			);
		}),
		catchError(err => of(actions.logoutFail(err))),
	)
);

export const emailLoginCall = action$ => (
	action$.pipe(
		ofType(types.EMAIL_LOGIN_CALL),
		switchMap(({ payload }) => (
			fromPromise(session.emailLogin(payload)).pipe(
				switchMap(res => {
					if (payload.onSuccess) payload.onSuccess(res);
					if (res?.is_registering && res?.registration_link_id) {
						reactLocalStorage.set('registrationLinkIdAfterLogin', res.registration_link_id);
					} else {
						reactLocalStorage.remove('registrationLinkIdAfterLogin');
					}

					const baseActions = [
						actions.setToken({ token: res.access_token, isJwtToken: true }),
						actions.checkLoginCall({ fromLogin: true }),
						actions.emailLoginDone(res),
					];

					return payload.hiddenToast !== true ? [
						...baseActions,
						actions.toastShowCall({ type: TOAST_TYPE_SUCCESS, message: 'share:auth:message:login_success' }),
					] : baseActions;
				}),
				catchError(error => {
					if (payload.onError) payload.onError(error.response);
					return of(actions.emailLoginFail(error));
				}),
			)
		)),
	)
);

export const emailRegisterCall = action$ => (
	action$.pipe(
		ofType(types.EMAIL_REGISTER_CALL),
		switchMap(({ payload }) => (
			fromPromise(session.emailRegister(payload)).pipe(
				switchMap(res => [
					actions.setModal({ modalData: payload }),
					actions.setModalType({ type: 'resendCertificationLetter' }),
					actions.emailRegisterDone(res),
				]),
				catchError(err => of(actions.emailRegisterFail(err))),
			)
		)),
	)
);

export const emailConfirmCall = action$ => (
	action$.pipe(
		ofType(types.EMAIL_CONFIRM_CALL),
		switchMap(({ payload }) => (
			fromPromise(session.emailConfirm(payload)).pipe(
				switchMap(res => [
					actions.setToken({ token: res.access_token, isJwtToken: true }),
					actions.emailConfirmDone(res),
					actions.checkLoginCall({ fromLogin: false }),
					actions.toastShowCall({ type: TOAST_TYPE_SUCCESS, message: 'share:auth:message:login_success' }),
					actions.historyPushCall('/'),
				]),
				catchError(err => merge(
					of(actions.emailConfirmFail(err)),
					of(actions.historyPushCall('/')),
					of(actions.toastShowCall({ message: 'share:auth:check_email:warn', type: TOAST_TYPE_ERROR })),
				)),
			)
		)),
	)
);

export const emailResendCall = (action$, store) => (
	action$.pipe(
		ofType(types.EMAIL_RE_SEND_CALL),
		switchMap(({ payload }) => (
			fromPromise(session.emailReSend(payload)).pipe(
				switchMap(res => [
					actions.emailReSendDone(res),
					actions.toastShowCall({ message: 'share:auth:check_email:success', type: TOAST_TYPE_SUCCESS }),
					actions.setModal({
						modalData: {
							...store.value.ui.modalData,
							message: {
								type: 'success',
								text: 'share:auth:check_email:success',
							},
						},
					}),
				]),
				catchError(err => of(actions.emailReSendFail(err))),
			)
		)),
	)
);

export const updateMobileCall = action$ => (
	action$.pipe(
		ofType(types.UPDATE_MOBILE_CALL),
		switchMap(({ payload, payload: { showToast } }) => (
			fromPromise(session.updateMobile(payload)).pipe(
				switchMap(res => {
					const newActions = [actions.updateMobileDone(res)];

					if (showToast) {
						newActions.push(
							actions.toastShowCall({ message: 'share:auth:confirm_mobile:success', type: TOAST_TYPE_SUCCESS }),
						);
					}

					return newActions;
				}),
				catchError(err => of(actions.updateMobileFail(err))),
			)
		)),
	)
);

export const confirmMobileCall = action$ => (
	action$.pipe(
		ofType(types.CONFIRM_MOBILE_CALL),
		switchMap(({ payload }) => (
			fromPromise(session.confirmMobile(payload)).pipe(
				switchMap(res => [
					actions.checkLoginCall({ fromLogin: false }),
					actions.confirmMobileDone(res),
				]),
				catchError(err => {
					ui.toastShow({ message: '驗證碼錯誤', type: TOAST_TYPE_ERROR });

					return of(actions.confirmMobileFail(err));
				}),
			)
		)),
	)
);

export const forgetPasswordCall = (action$, store) => (
	action$.pipe(
		ofType(types.FORGET_PASSWORD_CALL),
		switchMap(({ payload }) => (
			fromPromise(session.forgetPassword(payload)).pipe(
				switchMap(res => {
					const { onSuccess } = payload || {};
					if (onSuccess) {
						onSuccess();
					}
					return [
						actions.forgetPasswordDone(res),
						actions.setModal({
							modalData: {
								...store.value.ui.modalData,
								message: {
									type: 'success',
									text: 'share:auth:forget_password:success',
								},
							},
						}),
					];
				}),
				catchError(err => {
					const { onError } = payload || {};
					if (onError) {
						onError(err?.response?.data);
					}
					return of(actions.forgetPasswordFail(err));
				}),
			)
		)),
	)
);

export const getForgetPasswordEmailCall = action$ => (
	action$.pipe(
		ofType(types.GET_FORGET_PASSWORD_EMAIL_CALL),
		switchMap(({ payload, payload: { verification_code } }) => (
			fromPromise(session.getForgetPasswordEmail(payload)).pipe(
				switchMap(res => [
					actions.getForgetPasswordEmailDone(res),
					actions.historyPushCall({ pathname: '/training' }),
					actions.setModal({
						modalData: {
							isSuccess: true,
							email: res.email,
							verificationCode: verification_code,
						},
					}),
					actions.setModalType({ type: 'resetPassword' }),
				]),
				catchError(err => merge(
					of(actions.getForgetPasswordEmailFail(err)),
					of(actions.historyPushCall({ pathname: '/training' })),
					of(actions.toastShowCall({ message: 'share:auth:reset_password:link_error', type: TOAST_TYPE_ERROR })),
				)),
			)
		)),
	)
);

export const googleDomainNotAllow = action$ => (
	action$.pipe(
		ofType(types.GOOGLE_DOMAIN_NOT_ALLOW),
		mergeMap(() => merge(
			of(actions.historyPushCall({ pathname: '/business-login' })),
			of(actions.toastShowCall({ message: 'share:auth:employee_login:google_domain_not_allow', type: TOAST_TYPE_ERROR })),
		)),
	)
);

export const lineHrBindingNotFound = action$ => (
	action$.pipe(
		ofType(types.LINE_HR_BINDING_NOT_FOUND),
		mergeMap(({ payload }) => {
			const cb = takeLineLoginCallback(payload?.lineLoginState);
			return of(actions.historyPushCall({
				pathname: '/business-login',
				query: { step: 'line_login', toast: 'line_hr_binding_not_found', ...(cb ? { cb } : {}) },
			}));
		}),
	)
);

export const resetPasswordCall = action$ => (
	action$.pipe(
		ofType(types.RESET_PASSWORD_CALL),
		switchMap(({ payload }) => (
			fromPromise(session.resetPassword(payload)).pipe(
				switchMap(res => {
					const { onSuccess } = payload;
					if (onSuccess) {
						onSuccess();
					}
					return [
						actions.setToken({ token: res.access_token, isJwtToken: true }),
						actions.checkLoginCall({ fromLogin: true }),
						actions.resetPasswordDone(res),
						actions.toastShowCall({ message: 'share:auth:reset_password:success', type: TOAST_TYPE_SUCCESS }),
						actions.setModalType({ type: null }),
					];
				}),
				catchError(err => {
					const errorData = err?.response?.data;
					const passwordPolicyErrorKey = getPasswordPolicyErrorMessageKey(errorData?.error_type);
					const { onError } = payload || {};

					if (onError) {
						onError(errorData);
						return of(actions.resetPasswordFail(err));
					}

					return merge(
						of(actions.resetPasswordFail(err)),
						of(actions.toastShowCall({
							message: passwordPolicyErrorKey || 'share:auth:reset_password:link_error',
							type: TOAST_TYPE_ERROR,
						})),
					);
				}),
			)
		)),
	)
);

export const verifyPasswordCall = action$ => (
	action$.pipe(
		ofType(types.VERIFY_PASSWORD_CALL),
		switchMap(({ payload }) => (
			fromPromise(session.verifyPassword(payload)).pipe(
				switchMap(res => {
					if (typeof payload.onSuccess === 'function') payload.onSuccess();
					return [actions.verifyPasswordDone(res)];
				}),
				catchError(err => {
					if (typeof payload.onError === 'function') payload.onError(err.response);
					return of(actions.verifyPasswordFail(err));
				}),
			)
		)),
	)
);

export const verifyIdNumberCall = action$ => (
	action$.pipe(
		ofType(types.VERIFY_ID_NUMBER_CALL),
		switchMap(({ payload }) => (
			fromPromise(session.verifyIdNumber({
				id_number_last_four: payload.idNumberLastFour,
			})).pipe(
				switchMap(res => {
					if (typeof payload.onSuccess === 'function') payload.onSuccess(res);
					return [actions.verifyIdNumberDone(res)];
				}),
				catchError(err => {
					if (typeof payload.onError === 'function') payload.onError(err.response);
					return of(actions.verifyIdNumberFail(err));
				}),
			)
		)),
	)
);

export const socialLoginPrepareCall = action$ => (
	action$.pipe(
		ofType(types.SOCIAL_LOGIN_PREPARE_CALL),
		switchMap(({ payload }) => {
			clearLineLoginCallback();
			return fromPromise(session.socialLoginPrepare(payload)).pipe(
				switchMap(res => {
					let { url } = res;
					if (payload.name === 'line_hr') {
						saveLineLoginCallback(payload.cb, url);
					}
					if (payload.name === 'google') {
						const urlData = url.split('?');
						url = `${urlData[0]}?disallow_webview=true&${urlData[1]}`;
					}
					window.location.href = url;
					return [actions.socialLoginPrepareDone(res)];
				}),
				catchError(err => of(actions.socialLoginPrepareFail(err))),
			);
		}),
	)
);

export const socialLoginCall = action$ => (
	action$.pipe(
		ofType(types.SOCIAL_LOGIN_CALL),
		switchMap(({ payload }) => (
			fromPromise(
				session.socialLogin(payload)
					.then(res => session.oauthExchange({ code: res.exchange_code })
						.then(exchangeRes => ({ res, exchangeRes }))),
			).pipe(
				switchMap(({ res, exchangeRes }) => [
					actions.setToken({ token: exchangeRes.access_token, isJwtToken: true }),
					actions.checkLoginCall({ fromLogin: true }),
					actions.socialLoginDone(res),
					actions.toastShowCall({ type: TOAST_TYPE_SUCCESS, message: 'share:auth:message:login_success' }),
				]),
				catchError(err => [
					actions.socialLoginFail(err),
					actions.historyPushCall('/login-page'),
					actions.toastShowCall({ message: 'share:auth:message:login_error', type: TOAST_TYPE_ERROR }),
				]),
			)
		)),
	)
);

export const oauthExchangeCall = action$ => (
	action$.pipe(
		ofType(types.OAUTH_EXCHANGE_CALL),
		switchMap(({ payload }) => (
			fromPromise(session.oauthExchange({ code: payload.code })).pipe(
				switchMap(res => [
					actions.setToken({ token: res.access_token, isJwtToken: true }),
					actions.oauthExchangeDone(res),
					actions.checkLoginSessionCall({ cb: payload.cb, lineLoginState: payload.lineLoginState }),
				]),
				catchError(err => {
					clearLineLoginCallback(payload.lineLoginState);
					return of(actions.oauthExchangeFail(err));
				}),
			)
		)),
	)
);

export const sendBugMsgCall = action$ => (
	action$.pipe(
		ofType(types.SEND_BUG_MSG_CALL),
		switchMap(({ payload }) => (
			fromPromise(session.sendBugMsg(payload)).pipe(
				switchMap(res => [actions.sendBugMsgDone(res)]),
				catchError(err => of(actions.sendBugMsgFail(err))),
			)
		)),
	)
);

export const setBindResumeCall = action$ => (
	action$.pipe(
		ofType(types.SET_TOKEN, types.CHECK_LOGIN_DONE, types.POST_JOB_APPLY_DONE),
		switchMap(() => {
			const isSignedIn = reactLocalStorage.get('isSignedIn');
			if (!isSignedIn || isSignedIn === 'false') {
				return [];
			}
			const afterActions = [];
			const resumeExpiredAt = reactLocalStorage.get('resume_id_expiredAt');
			if (resumeExpiredAt && (new Date()).getTime() < parseInt(resumeExpiredAt)) {
				const resumeIds = JSON.parse(reactLocalStorage.get('resume_id', JSON.stringify([])));
				// eslint-disable-next-line no-restricted-syntax
				for (const resumeId of resumeIds) {
					afterActions.push(actions.postJobBingingCall({ id: resumeId }));
				}
				reactLocalStorage.remove('resume_id');
				reactLocalStorage.remove('resume_id_expiredAt');
			}
			return afterActions;
		}),
	)
);

export const rfaLoginCall = action$ => (
	action$.pipe(
		ofType(types.RFA_LOGIN_CALL),
		switchMap(({ payload }) => (
			fromPromise(session.rfaLogin(payload)).pipe(
				switchMap(res => {
					if (payload.onSuccess) payload.onSuccess();
					return [
						actions.setToken({ token: res.access_token, isJwtToken: true }),
						actions.checkLoginCall({ fromLogin: true }),
						actions.rfaLoginDone(res),
						actions.toastShowCall({ type: TOAST_TYPE_SUCCESS, message: 'share:auth:message:login_success' }),
					];
				}),
				catchError(err => of(actions.rfaLoginFail(err))),
			)
		)),
	)
);

export const lineTokenLoginCall = action$ => (
	action$.pipe(
		ofType(types.LINE_TOKEN_LOGIN_CALL),
		switchMap(({ payload }) => (
			fromPromise(session.lineTokenLogin({
				hr_line_id_token: payload.hr_line_id_token,
				...(payload.force_link_rich_menu ? { force_link_rich_menu: true } : {}),
			})).pipe(
				switchMap(res => [
					actions.setToken({ token: res.access_token, isJwtToken: true }),
					actions.checkLoginCall({
						fromLogin: true,
						onSuccess: () => payload.onSuccess?.(res),
						onError: payload.onError,
					}),
					actions.lineTokenLoginDone(res),
					actions.toastShowCall({ type: TOAST_TYPE_SUCCESS, message: 'share:auth:message:login_success' }),
				]),
				catchError(err => {
					if (payload.onError) payload.onError(err);
					return of(actions.lineTokenLoginFail(err));
				}),
			)
		)),
	)
);

export const forceLinkRichMenuCall = action$ => (
	action$.pipe(
		ofType(types.FORCE_LINK_RICH_MENU_CALL),
		switchMap(({ payload }) => (
			fromPromise(session.bindLine({
				hr_line_id_token: payload.hr_line_id_token,
				force_link_rich_menu: true,
			})).pipe(
				switchMap(res => [actions.forceLinkRichMenuDone(res)]),
				catchError(err => {
					if (err?.response?.status === 400) {
						return [
							actions.toastShowCall({ type: TOAST_TYPE_ERROR, message: 'share:auth:message:line_account_mismatch' }),
							actions.forceLinkRichMenuFail(err),
						];
					}
					return of(actions.forceLinkRichMenuFail(err));
				}),
			)
		)),
	)
);

export const checkMobileLineBoundCall = action$ => (
	action$.pipe(
		ofType(types.CHECK_MOBILE_LINE_BOUND_CALL),
		switchMap(({ payload }) => (
			fromPromise(session.checkMobileLineBound(payload)).pipe(
				switchMap(res => {
					if (payload.onSuccess) payload.onSuccess(res);
					return [actions.checkMobileLineBoundDone(res)];
				}),
				catchError(err => {
					if (payload.onError) payload.onError(err);
					return of(actions.checkMobileLineBoundFail(err));
				}),
			)
		)),
	)
);
